import type {
  ChainEnum,
  TokenScope,
  VerifyResponse,
  WalletAddressType,
  WalletProviderEnum,
} from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../client/core/getCore';
import type { DynamicClient } from '../../../client/types';
import { isErrorWithCode } from '../../../utils/isErrorWithCode';
import { createApiClient } from '../../apiClient';
import { consumeCaptchaToken } from '../../captcha/consumeCaptchaToken';
import type { Chain } from '../../chain';
import { normalizeWalletNameWithChain } from '../utils/normalizeWalletNameWithChain';
import type { WalletAddressWithType } from '../walletAccount/walletAccount.types';
import { WalletAlreadyLinkedToAnotherUserError } from './../../../errors/WalletAlreadyLinkedToAnotherUserError';

type VerifyMessageSignatureOwnershipParams = {
  addressesWithTypes?: WalletAddressWithType[];
  chain: Chain;
  isTransfer?: boolean;
  messageToSign: string;
  networkId?: string;
  requestedScopes?: TokenScope[];
  signature: string;
  walletAddress: string;
  walletDisplayName: string;
  walletProviderType: WalletProviderEnum;
};

/**
 * Verifies a signed message by calling either the verifyLink, verifyTransfer or verify API function,
 * depending on whether the user is signing in, linking or transferring a wallet.
 *
 * Does NOT call updateAuthFromVerifyResponse, it should be called from the return of this function.
 */
export const verifyMessageSignatureOwnership = async (
  {
    messageToSign,
    walletAddress,
    addressesWithTypes,
    signature,
    chain,
    isTransfer = false,
    walletDisplayName,
    walletProviderType,
    networkId,
    requestedScopes,
  }: VerifyMessageSignatureOwnershipParams,
  client: DynamicClient
): Promise<VerifyResponse> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  const walletName = normalizeWalletNameWithChain({
    chain,
    displayName: walletDisplayName,
  });

  let verifyApiFunction;

  if (!client.user) {
    // If the user is not authenticated, call the walletsSignin API function to sign in
    verifyApiFunction = apiClient.walletsSignin.bind(apiClient);
  } else if (isTransfer) {
    // If the user is authenticated and is transferring a wallet, call the verifyTransfer API function
    verifyApiFunction = apiClient.verifyTransfer.bind(apiClient);
  } else if (client.user && requestedScopes) {
    // Scopes = step-up auth: user requests permission for a sensitive op (e.g. wallet export).
    // We grant it only if they prove they own an existing credential (walletsVerify).
    // Must use walletsVerify: user is already logged in (not signin) and credential exists (not transfer/link).
    // If the user is authenticated and requested scopes are provided, call the walletsVerify API function
    verifyApiFunction = apiClient.walletsVerify.bind(apiClient);
  } else {
    // If the user is authenticated and is signing in, call the verifyLink API function
    verifyApiFunction = apiClient.verifyLink.bind(apiClient);
  }

  try {
    return await verifyApiFunction({
      environmentId: core.environmentId,
      verifyRequest: {
        // mapping to handle typecasting for address type
        additionalWalletAddresses: addressesWithTypes?.map((address) => ({
          address: address.address,
          publicKey: address.publicKey,
          type: address.type as WalletAddressType,
        })),
        captchaToken: consumeCaptchaToken(client),
        // eslint-disable-next-line custom-rules/ban-chain-enum
        chain: chain as ChainEnum,
        messageToSign,
        network: networkId,
        publicWalletAddress: walletAddress,
        requestedScopes,
        signedMessage: signature,
        walletName,
        walletProvider: walletProviderType,
      },
    });
  } catch (error) {
    if (
      isErrorWithCode(error, 'reassign_wallet_confirm') ||
      isErrorWithCode(error, 'merge_accounts_confirmation')
    ) {
      throw new WalletAlreadyLinkedToAnotherUserError({
        cause: error,
      });
    }

    throw error;
  }
};
